import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { makePrioStyles } from '../../theme/utils';
import { Button, InformationBox } from '@prio365/prio365-react-library';
import { Project } from '../../models/Project';
import { ProjectExtensionType } from '../../models/ProjectExtension';
import { RootReducerState } from '../../apps/main/rootReducer';
import { createSelector } from 'reselect';
import { useContactsContext } from '../contacts/hooks/useContactsProvider';
import TaskHubDashboard from './TaskHubDashboard';
import TaskHubWelcome from './TaskHubWelcome';

import { sortProjects } from '../../modules/projects/utils';
import {
  getAllTasksFromBoards,
  ExtendedBoardDto,
  ExtendedTaskDto,
  apiFetchBoardsForProjects,
} from './api/taskHubApi';
import {
  TaskHubModuleSkeleton,
  TaskHubDashboardSkeleton,
} from './TaskHubSkeletons';
import { useTaskHubSettings } from './setting/useTaskHubSettings';
import {
  TaskHubFilters,
  TaskHubModuleFilters,
} from './setting/TaskHubSettings.types';
import TaskHubHeader from './TaskHubHeader';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.colors.application.background.light,
  },
  content: {
    flex: 1,
    padding: theme.spacing.regular,
    overflow: 'auto',
  },
  errorContainer: {
    marginTop: theme.spacing.regular,
    padding: theme.spacing.regular,
    backgroundColor: theme.colors.base.red['20'],
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.base.red.default}`,
  },
  errorTitle: {
    fontWeight: 600,
    color: theme.colors.base.red.default,
    marginBottom: theme.spacing.small,
  },
  errorMessage: {
    color: theme.colors.base.red.default,
    fontSize: '14px',
  },
  executionPromptContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: theme.spacing.large,
    textAlign: 'center',
    backgroundColor: theme.colors.application.background.default,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
  },
  executionPromptIcon: {
    fontSize: '48px',
    color: theme.colors.base.primary.default,
    marginBottom: theme.spacing.regular,
  },
  executionPromptTitle: {
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: theme.spacing.small,
    color: theme.colors.application.typography.default,
  },
  executionPromptDescription: {
    fontSize: '16px',
    color: theme.colors.application.typography.muted,
    marginBottom: theme.spacing.large,
    maxWidth: '500px',
  },
  executionPromptButton: {
    minWidth: '150px',
  },
}));

export interface TaskSummaryDto {
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  tasksByStatus: Record<string, number>;
  tasksByPriority: Record<string, number>;
  tasksByAssignee: Record<string, number>;
  tasksByProject: Record<string, number>;
}

const getProjectExtensionMe = (
  project: Project,
  type: ProjectExtensionType
) => {
  if (!project) {
    return undefined;
  }
  return project.projectExtensionMeDtos?.find(
    (ext) => ext.projectExtensionType === type
  );
};

const myProjectsWithPlannerSelector = createSelector<
  [
    (state: RootReducerState) => string[],
    (state: RootReducerState) => Record<string, Project>,
  ],
  Project[]
>(
  (state) => state.projects.projects.myIds,
  (state) => state.projects.projects.byId,
  (ids, byId) => {
    if (!ids || ids.length === 0) {
      return [];
    }

    const projectsWithPlanner = (ids ?? [])
      .map((id) => byId[id])
      .filter((project) => {
        const plannerExtension = getProjectExtensionMe(project, 'planner');
        return !!plannerExtension && !project.isArchived;
      });

    return sortProjects(projectsWithPlanner);
  }
);

// Simplified summary calculation using ExtendedTaskDto
const calculateTaskSummary = (tasks: ExtendedTaskDto[]): TaskSummaryDto => {
  const summary: TaskSummaryDto = {
    totalTasks: tasks.length,
    completedTasks: tasks.filter((t) => t.status === 'completed').length,
    overdueTasks: tasks.filter(
      (t) => t.dueDate && new Date(t.dueDate) < new Date()
    ).length,
    tasksByStatus: {},
    tasksByPriority: {},
    tasksByAssignee: {},
    tasksByProject: {},
  };

  tasks.forEach((task) => {
    // Status
    summary.tasksByStatus[task.status] =
      (summary.tasksByStatus[task.status] || 0) + 1;

    // Priority
    summary.tasksByPriority[task.priority] =
      (summary.tasksByPriority[task.priority] || 0) + 1;

    // Assignees
    if (task.assignees.length > 0) {
      task.assignees.forEach((assignee) => {
        summary.tasksByAssignee[assignee] =
          (summary.tasksByAssignee[assignee] || 0) + 1;
      });
    } else {
      summary.tasksByAssignee['Unassigned'] =
        (summary.tasksByAssignee['Unassigned'] || 0) + 1;
    }

    // Projects - using the enriched projectDisplayName
    const projectName = task.projectDisplayName || 'Unknown Project';
    summary.tasksByProject[projectName] =
      (summary.tasksByProject[projectName] || 0) + 1;
  });

  return summary;
};

interface TaskHubModuleProps {
  className?: string;
}

const TaskHubModule: React.FC<TaskHubModuleProps> = ({ className }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getContactById } = useContactsContext();

  // State
  const [selectedPlannerIds, setSelectedPlannerIds] = useState<string[]>([]);
  const [executedPlannerIds, setExecutedPlannerIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tasks, setTasks] = useState<ExtendedTaskDto[]>([]);
  const [summary, setSummary] = useState<TaskSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TaskHubModuleFilters>({});

  const projectsWithPlanner = useSelector(myProjectsWithPlannerSelector);

  // Settings Hook
  const { defaultQuery, loading: settingsLoading } = useTaskHubSettings();

  // Helper to enrich boards with project information
  const enrichBoardsWithProjectInfo = useCallback(
    (boards: ExtendedBoardDto[]): ExtendedBoardDto[] => {
      return boards.map((board) => {
        // Find the project that matches this board's projectExtensionId
        const project = projectsWithPlanner.find((p) =>
          p.projectExtensionMeDtos?.some((ext) => ext.key === board.boardId)
        );

        if (project) {
          return {
            ...board,
            projectId: project.projectId,
            projectName: project.name,
            projectNumber: project.shortName,
          };
        }
        return board;
      });
    },
    [projectsWithPlanner]
  );

  const plannerOptions = useMemo(() => {
    const options: {
      value: string;
      label: string;
      projectId: string;
      projectName: string;
    }[] = [];

    projectsWithPlanner.forEach((project) => {
      const plannerExtensions =
        project.projectExtensionMeDtos?.filter(
          (ext) => ext.projectExtensionType === 'planner'
        ) || [];

      plannerExtensions.forEach((extension) => {
        if (extension.key) {
          const projectDisplayName = project.shortName || project.name;
          options.push({
            value: extension.key,
            label: `${project.number} - ${projectDisplayName}`,
            projectId: project.projectId,
            projectName: project.name,
          });
        }
      });
    });

    return options;
  }, [projectsWithPlanner]);

  // Auto-load default query on start
  useEffect(() => {
    if (defaultQuery && !settingsLoading) {
      // Convert TaskHubQuery to module filters format
      setFilters({
        assignee: defaultQuery.assigneeIds,
        project: defaultQuery.projectIds,
        priority: defaultQuery.priorityIds,
        labels: defaultQuery.labelIds,
        status: defaultQuery.statusIds,
      });

      if (defaultQuery.plannerIds.length > 0) {
        setSelectedPlannerIds(defaultQuery.plannerIds);
      }
    }
  }, [defaultQuery, settingsLoading]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const plannerIds =
      searchParams.get('planners')?.split(',').filter(Boolean) || [];
    if (plannerIds.length > 0) {
      setSelectedPlannerIds(plannerIds);
      setExecutedPlannerIds(plannerIds);
    }
  }, [searchParams]);

  const updateSearchParams = useCallback(
    (plannerIds: string[]) => {
      const params = new URLSearchParams();
      if (plannerIds.length > 0) {
        params.set('planners', plannerIds.join(','));
      }
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handlePlannerChange = useCallback((plannerIds: string[]) => {
    // Limit to maximum 15 planners
    if (plannerIds.length <= 15) {
      setSelectedPlannerIds(plannerIds);
    }
  }, []);

  // Convert module filters to TaskHub filters format for saving
  const getCurrentFiltersForSaving = useCallback((): TaskHubFilters => {
    return {
      plannerIds: selectedPlannerIds,
      assigneeIds: filters.assignee || [],
      projectIds: filters.project || [],
      priorityIds: filters.priority || [],
      labelIds: filters.labels || [],
      statusIds: filters.status || [],
    };
  }, [selectedPlannerIds, filters]);

  const handleExecuteOrRefresh = useCallback(async () => {
    const plannerIdsToUse =
      selectedPlannerIds.length > 0 ? selectedPlannerIds : executedPlannerIds;

    if (plannerIdsToUse.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);
    setExecutedPlannerIds(plannerIdsToUse);
    updateSearchParams(plannerIdsToUse);

    try {
      const boardsResponse = await apiFetchBoardsForProjects(plannerIdsToUse);

      if (!boardsResponse.result.ok) {
        throw new Error(
          `API Error: ${boardsResponse.result.status} ${boardsResponse.result.statusText}`
        );
      }

      const fetchedBoards = boardsResponse.data || [];

      // Enrich boards with project information
      const enrichedBoards = enrichBoardsWithProjectInfo(fetchedBoards);

      // Extract all tasks from boards and resolve assignee names + enrich with project info
      const allTasks = getAllTasksFromBoards(enrichedBoards).map((task) => {
        // Resolve assignee names
        const resolvedAssignees = task.assignees.map((assigneeId) => {
          const contact = getContactById(assigneeId);
          return contact
            ? `${contact.firstName} ${contact.lastName}`
            : assigneeId;
        });

        // Use the enriched project information
        return {
          ...task,
          assignees: resolvedAssignees,
          projectDisplayName:
            task.projectName || task.projectDisplayName || 'Unknown Project',
        };
      });

      // Calculate summary - much simpler now
      const calculatedSummary = calculateTaskSummary(allTasks);

      // Update state
      setTasks(allTasks);
      setSummary(calculatedSummary);
      setError(null);
    } catch (error: any) {
      console.error('TaskHub Error:', error);
      setError(error.message || 'Failed to fetch task data');
      setTasks([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [
    selectedPlannerIds,
    executedPlannerIds,
    updateSearchParams,
    getContactById,
    enrichBoardsWithProjectInfo,
  ]);

  const handleChartInteraction = useCallback(
    (filterType: string, filterValue: string) => {},
    []
  );

  const handleTaskDetail = useCallback((task: ExtendedTaskDto) => {
    if (task.url) {
      window.open(task.url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleWelcomeGetStarted = useCallback(() => {
    // Scroll to header since we no longer have separate selector
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const shouldShowExecutionPrompt = useMemo(() => {
    const hasQueryParams =
      searchParams.get('planners')?.split(',').filter(Boolean).length > 0;
    return hasQueryParams && executedPlannerIds.length === 0;
  }, [searchParams, executedPlannerIds]);

  const buttonMode = useMemo(() => {
    if (executedPlannerIds.length === 0 || selectedPlannerIds.length === 0) {
      return 'execute';
    }

    const hasChanges =
      selectedPlannerIds.length !== executedPlannerIds.length ||
      !selectedPlannerIds.every((id) => executedPlannerIds.includes(id));

    return hasChanges ? 'execute' : 'refresh';
  }, [selectedPlannerIds, executedPlannerIds]);

  if (initialLoading) {
    return <TaskHubModuleSkeleton />;
  }

  return (
    <div className={`${classes.root} ${className || ''}`}>
      {/* Use the new TaskHubHeader component */}
      <TaskHubHeader
        plannerOptions={plannerOptions}
        selectedPlannerIds={selectedPlannerIds}
        onPlannerChange={handlePlannerChange}
        onExecute={handleExecuteOrRefresh}
        loading={loading}
        buttonMode={buttonMode}
        currentFilters={getCurrentFiltersForSaving()}
        onFiltersChange={(newFilters) => {
          setFilters({
            assignee: newFilters.assignee,
            project: newFilters.project,
            priority: newFilters.priority,
            labels: newFilters.labels,
            status: newFilters.status,
          });
          // Note: PlannerIds are handled separately through onPlannerChange
        }}
        executedPlannerIds={executedPlannerIds}
      />

      <div className={classes.content}>
        {error && (
          <div className={classes.errorContainer}>
            <div className={classes.errorTitle}>
              {t('taskHub:errors.loadingTaskData')}
            </div>
            <div className={classes.errorMessage}>{error}</div>
            <Button
              type="default"
              size="small"
              onClick={handleExecuteOrRefresh}
              style={{ marginTop: 8 }}
              iconProp={['fal', 'sync']}
            >
              {t('taskHub:errors.retry')}
            </Button>
          </div>
        )}

        {shouldShowExecutionPrompt ? (
          <div className={classes.executionPromptContainer}>
            <div className={classes.executionPromptIcon}>
              <i className="fal fa-play-circle" />
            </div>
            <h2 className={classes.executionPromptTitle}>
              {t('taskHub:executionPrompt.title')}
            </h2>
            <p className={classes.executionPromptDescription}>
              {t('taskHub:executionPrompt.description')}
            </p>
            <Button
              type="primary"
              size="large"
              className={classes.executionPromptButton}
              onClick={handleExecuteOrRefresh}
              iconProp={['fal', 'play']}
              disabled={selectedPlannerIds.length === 0 || loading}
              loading={loading}
            >
              {t('taskHub:executionPrompt.executeButton')}
            </Button>
          </div>
        ) : executedPlannerIds.length === 0 ? (
          <TaskHubWelcome
            availableProjectsCount={plannerOptions.length}
            onGetStarted={handleWelcomeGetStarted}
          />
        ) : loading ? (
          <TaskHubDashboardSkeleton />
        ) : !error && summary ? (
          <TaskHubDashboard
            summary={summary}
            allTasks={tasks}
            onChartInteraction={handleChartInteraction}
            onTaskDetail={handleTaskDetail}
            getContactById={getContactById}
            loading={false}
          />
        ) : (
          !error &&
          tasks.length === 0 &&
          executedPlannerIds.length > 0 && (
            <InformationBox
              title={t('taskHub:noTasks.title')}
              description={t('taskHub:noTasks.description')}
            />
          )
        )}
      </div>
    </div>
  );
};

export default TaskHubModule;
